import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { InspectionModel } from 'shared/domain/inspection/inspectionModel';
import { ProtocolAnswer } from 'shared/domain/protocolAnswer';
import {
  collectChecklistItemIdsInSectionTree,
  collectProtocolItemIdsInSectionTree,
} from './collectProtocolItemIdsInSectionTree';
import { resolveSectionBatchKeyId } from '../protocolSectionResolver';
import { sameProtocolTreeId } from './protocolTreeIds';

type ChecklistTreeNode = {
  _id: string;
  parentSectionId: string | null;
};

type ChecklistAnswerConfig = {
  _id: string;
  type: AnswerType;
  deleted?: boolean;
};

export type ChecklistItemWithAnswers = ChecklistTreeNode & {
  answers?: ChecklistAnswerConfig[];
};

function activeAnswerConfigs(
  answers: ChecklistAnswerConfig[] | undefined
): ChecklistAnswerConfig[] {
  return (answers ?? []).filter((answer) => !answer.deleted);
}

export function collectProtocolItemIdsForSectionKey(params: {
  inspectionModel: InspectionModel | undefined;
  sectionKeyId: string;
  checklistItems?: ChecklistTreeNode[];
  checklistSections?: ChecklistTreeNode[];
}): string[] {
  if (!params.inspectionModel) {
    return [];
  }

  const batchSectionId = resolveSectionBatchKeyId(
    params.inspectionModel,
    params.sectionKeyId
  );

  return collectProtocolItemIdsInSectionTree({
    protocolSectionId: batchSectionId,
    protocolSections: params.inspectionModel.sections ?? [],
    protocol: params.inspectionModel.protocol ?? [],
    checklistItems: params.checklistItems,
    checklistSections: params.checklistSections,
  });
}

export function collectProtocolAnswersInSectionTree(params: {
  inspectionModel: InspectionModel | undefined;
  sectionKeyId: string;
  checklistItems?: ChecklistTreeNode[];
  checklistSections?: ChecklistTreeNode[];
}): ProtocolAnswer[] {
  if (!params.inspectionModel) {
    return [];
  }

  const protocolItemIds = collectProtocolItemIdsForSectionKey(params);

  return protocolItemIds.flatMap((protocolItemId) => {
    const protocolItem = params.inspectionModel?.protocol?.find((item) =>
      sameProtocolTreeId(item._id, protocolItemId)
    );

    return protocolItem?.answers ?? [];
  });
}

function protocolAnswerForChecklistAnswer(
  inspectionModel: InspectionModel | undefined,
  checklistItemId: string,
  checklistAnswerId: string
): ProtocolAnswer | undefined {
  const protocolItem = inspectionModel?.protocol?.find((item) =>
    sameProtocolTreeId(item.templateChecklistItem, checklistItemId)
  );

  return protocolItem?.answers?.find((answer) =>
    sameProtocolTreeId(answer.checklistAnswerId, checklistAnswerId)
  );
}

export function sectionHasSkippableAnswerTypes(params: {
  inspectionModel: InspectionModel | undefined;
  sectionKeyId: string;
  checklistItems?: ChecklistItemWithAnswers[];
  checklistSections?: ChecklistTreeNode[];
}): boolean {
  const checklistItemIds = collectChecklistItemIdsInSectionTree({
    sectionKeyId: params.sectionKeyId,
    protocolSections: params.inspectionModel?.sections ?? [],
    checklistItems: params.checklistItems ?? [],
    checklistSections: params.checklistSections,
  });

  return checklistItemIds.some((checklistItemId) => {
    const checklistItem = params.checklistItems?.find((item) =>
      sameProtocolTreeId(item._id, checklistItemId)
    );

    return activeAnswerConfigs(checklistItem?.answers).length > 0;
  });
}

export function isSectionAnswersFullyNotApplicable(params: {
  inspectionModel: InspectionModel | undefined;
  sectionKeyId: string;
  checklistItems?: ChecklistItemWithAnswers[];
  checklistSections?: ChecklistTreeNode[];
}): boolean {
  const checklistItemIds = collectChecklistItemIdsInSectionTree({
    sectionKeyId: params.sectionKeyId,
    protocolSections: params.inspectionModel?.sections ?? [],
    checklistItems: params.checklistItems ?? [],
    checklistSections: params.checklistSections,
  });

  const answerConfigs = checklistItemIds.flatMap((checklistItemId) => {
    const checklistItem = params.checklistItems?.find((item) =>
      sameProtocolTreeId(item._id, checklistItemId)
    );

    return activeAnswerConfigs(checklistItem?.answers).map((answerConfig) => ({
      checklistItemId,
      checklistAnswerId: answerConfig._id,
    }));
  });

  if (answerConfigs.length === 0) {
    return false;
  }

  return answerConfigs.every(({ checklistItemId, checklistAnswerId }) => {
    const protocolAnswer = protocolAnswerForChecklistAnswer(
      params.inspectionModel,
      checklistItemId,
      checklistAnswerId
    );

    return protocolAnswer?.isApplicable === false;
  });
}

export type SectionApplicability = {
  /** Section tree contains at least one active answer config. */
  canSkip: boolean;
  /** Every active answer config in the section tree is marked not applicable. */
  isNotApplicable: boolean;
};

type AnswerCounts = { active: number; notApplicable: number };

/**
 * Same result as calling `sectionHasSkippableAnswerTypes` and
 * `isSectionAnswersFullyNotApplicable` for every key, in one linear pass.
 */
export function computeSectionApplicabilityById(params: {
  inspectionModel: InspectionModel | undefined;
  sectionKeyIds: readonly string[];
  checklistItems?: ChecklistItemWithAnswers[];
  checklistSections?: ChecklistTreeNode[];
}): Map<string, SectionApplicability> {
  const protocolSections = params.inspectionModel?.sections ?? [];
  const rootIdBySectionKey = new Map<string, string>();
  protocolSections.forEach((section) => {
    const rootId = section.checklistSectionId ?? section._id;
    if (!rootIdBySectionKey.has(section._id)) {
      rootIdBySectionKey.set(section._id, rootId);
    }
    if (
      section.checklistSectionId &&
      !rootIdBySectionKey.has(section.checklistSectionId)
    ) {
      rootIdBySectionKey.set(section.checklistSectionId, rootId);
    }
  });

  const answersByChecklistItemId = new Map<string, Map<string, ProtocolAnswer>>();
  (params.inspectionModel?.protocol ?? []).forEach((protocolItem) => {
    if (
      protocolItem.templateChecklistItem == null ||
      answersByChecklistItemId.has(protocolItem.templateChecklistItem)
    ) {
      return;
    }
    const answersById = new Map<string, ProtocolAnswer>();
    (protocolItem.answers ?? []).forEach((answer) => {
      if (
        answer.checklistAnswerId != null &&
        !answersById.has(answer.checklistAnswerId)
      ) {
        answersById.set(answer.checklistAnswerId, answer);
      }
    });
    answersByChecklistItemId.set(protocolItem.templateChecklistItem, answersById);
  });

  const ownCountsBySectionId = new Map<string, AnswerCounts>();
  (params.checklistItems ?? []).forEach((item) => {
    if (item.parentSectionId == null) {
      return;
    }
    const answersById = answersByChecklistItemId.get(item._id);
    const counts = ownCountsBySectionId.get(item.parentSectionId) ?? {
      active: 0,
      notApplicable: 0,
    };
    activeAnswerConfigs(item.answers).forEach((answerConfig) => {
      counts.active += 1;
      if (answersById?.get(answerConfig._id)?.isApplicable === false) {
        counts.notApplicable += 1;
      }
    });
    ownCountsBySectionId.set(item.parentSectionId, counts);
  });

  const childIdsBySectionId = new Map<string, string[]>();
  (params.checklistSections ?? []).forEach((section) => {
    if (section.parentSectionId == null) {
      return;
    }
    const childIds = childIdsBySectionId.get(section.parentSectionId) ?? [];
    childIds.push(section._id);
    childIdsBySectionId.set(section.parentSectionId, childIds);
  });

  const treeCountsBySectionId = new Map<string, AnswerCounts>();
  const countSectionTree = (rootId: string): AnswerCounts => {
    const cached = treeCountsBySectionId.get(rootId);
    if (cached) {
      return cached;
    }
    const total: AnswerCounts = { active: 0, notApplicable: 0 };
    const visited = new Set<string>([rootId]);
    const pending = [rootId];
    while (pending.length > 0) {
      const sectionId = pending.pop() as string;
      const own = ownCountsBySectionId.get(sectionId);
      if (own) {
        total.active += own.active;
        total.notApplicable += own.notApplicable;
      }
      (childIdsBySectionId.get(sectionId) ?? []).forEach((childId) => {
        if (!visited.has(childId)) {
          visited.add(childId);
          pending.push(childId);
        }
      });
    }
    treeCountsBySectionId.set(rootId, total);
    return total;
  };

  const result = new Map<string, SectionApplicability>();
  params.sectionKeyIds.forEach((sectionKeyId) => {
    const counts = countSectionTree(
      rootIdBySectionKey.get(sectionKeyId) ?? sectionKeyId
    );
    result.set(sectionKeyId, {
      canSkip: counts.active > 0,
      isNotApplicable:
        counts.active > 0 && counts.notApplicable === counts.active,
    });
  });
  return result;
}
